import { useCallback, useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { apolloClient } from '../../lib/apolloClient';
import { GET_CITIES } from '../../lib/graphql/queries/cities';

const PAGE_SIZE = 25;

export interface CityData extends Record<string, unknown> {
  id: string;
  name: string;
  state: string;
  stateFull: string;
  isCentered: boolean;
  coordinates: {
    lat: number;
    lng: number;
  };
}

interface UseCitiesDataProps {
  searchQuery?: string;
  organizationId?: string;
  offset?: number;
  pageSize?: number;
}

interface GqlCity {
  cityId: string;
  name?: string;
  stateCode?: string;
  stateName?: string;
  latitude?: number;
  longitude?: number;
}

const toCityData = (c: GqlCity): CityData => ({
  id: c.cityId,
  name: c.name || '',
  state: c.stateCode || '',
  stateFull: c.stateName || '',
  isCentered: false,
  coordinates: { lat: c.latitude || 0, lng: c.longitude || 0 },
});

const fetchCities = async (limit: number, offset: number, search?: string) => {
  const { data } = await apolloClient.query({
    query: GET_CITIES,
    variables: {
      limit,
      offset,
      sortOrder: 'asc',
      sortBy: 'name',
      // no isActive filter: City Management lists all cities (678), matching the
      // prior Firestore behaviour (isActive=true would return only ~1).
      search: search || undefined,
    },
    fetchPolicy: 'network-only',
  });
  return data.cities as { cities: GqlCity[]; total: number; hasMore: boolean };
};

const CITY_SEARCH_PAGE_SIZE = 50;

/**
 * Server-side city search for pickers: results come from the `cities` query's
 * `search` filter a page at a time, so the full city list is never loaded.
 * Same shape as useUniqueContactTags (search setter + loadMore).
 */
export const useCitySearch = () => {
  const [search, setSearch] = useState('');

  const query = useInfiniteQuery({
    queryKey: ['CitySearch', search],
    queryFn: ({ pageParam }) => fetchCities(CITY_SEARCH_PAGE_SIZE, pageParam, search),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) =>
      lastPage.hasMore ? pages.reduce((count, page) => count + page.cities.length, 0) : undefined,
    staleTime: 1000 * 60 * 30,
  });

  const cities = useMemo(
    () => query.data?.pages.flatMap((page) => page.cities.map(toCityData)) ?? [],
    [query.data]
  );

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    cities,
    isLoading: query.isLoading,
    hasMore: !!hasNextPage,
    loadMore,
    setSearch,
  };
};

/**
 * Cities for the City Management panel — offset/page-based off the Spanner
 * `cities(limit, offset)` GraphQL query (Cities are no longer in Firestore, so
 * we page by offset rather than a forward-only cursor). Returns a single page
 * plus the total so the caller can drive OffsetPagination.
 */
export const useCitiesData = ({
  searchQuery,
  organizationId,
  offset = 0,
  pageSize = PAGE_SIZE,
}: UseCitiesDataProps = {}) => {
  const query = useQuery({
    queryKey: ['Cities', searchQuery, organizationId, offset, pageSize],
    queryFn: async () => {
      const conn = await fetchCities(pageSize, offset, searchQuery);
      return { cities: conn.cities.map(toCityData), total: conn.total };
    },
    // Keep the previous page visible while the next one loads so paging doesn't
    // flash an empty table.
    placeholderData: (previous) => previous,
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    retry: 1,
    enabled: Boolean(organizationId),
  });

  return {
    cities: query.data?.cities ?? [],
    totalCount: query.data?.total ?? 0,
    isLoading: query.isLoading || query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
};
